import { useState } from 'react';
import { X, MessageSquare, Sparkles, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import updates from '../data/updates.json';
import { api } from '../lib/api';
import clsx from 'clsx';

export default function FeedbackModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('updates');
    const [formData, setFormData] = useState({ type: 'Feature Request', description: '' });
    const [status, setStatus] = useState('idle'); // idle, loading, success, error

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');

        try {
            await api.post('/feedback', formData);

            setStatus('success');
            setFormData({ type: 'Feature Request', description: '' });
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error) {
            console.error('Feedback submission error:', error);
            setStatus('error');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div
                className="bg-white dark:bg-surface-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-surface-100 dark:border-surface-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-500/10 rounded-xl flex items-center justify-center text-brand-600 dark:text-brand-400">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">EdFrame Updates</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">What's new & Feedback</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-full transition-colors"
                    >
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex p-1 bg-surface-50 dark:bg-surface-900 mx-6 mt-6 rounded-xl">
                    <button
                        onClick={() => setActiveTab('updates')}
                        className={clsx(
                            "flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all",
                            activeTab === 'updates'
                                ? "bg-white dark:bg-surface-700 text-slate-900 dark:text-white shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        )}
                    >
                        <Sparkles size={16} />
                        Updates
                    </button>
                    <button
                        onClick={() => setActiveTab('feedback')}
                        className={clsx(
                            "flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all",
                            activeTab === 'feedback'
                                ? "bg-white dark:bg-surface-700 text-slate-900 dark:text-white shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        )}
                    >
                        <MessageSquare size={16} />
                        Message Us
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[400px]">
                    {activeTab === 'updates' ? (
                        <div className="space-y-6">
                            {updates.map((update, i) => (
                                <div key={i} className="group relative pl-6 border-l-2 border-surface-100 dark:border-surface-700 pb-1">
                                    <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-brand-500 ring-4 ring-white dark:ring-surface-800" />
                                    <div className="flex items-baseline justify-between mb-1">
                                        <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                            {update.title}
                                        </h3>
                                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{update.date}</span>
                                    </div>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                        {update.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {status === 'success' ? (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="w-16 h-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mb-4">
                                        <CheckCircle2 size={32} />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Message Sent!</h3>
                                    <p className="text-sm text-slate-500">Thanks for the feedback. We'll check it out!</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                            Feedback Type
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['Feature Request', 'Bug Report'].map(type => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, type })}
                                                    className={clsx(
                                                        "py-2 px-3 text-sm font-medium rounded-lg border-2 transition-all text-center",
                                                        formData.type === type
                                                            ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
                                                            : "border-surface-100 dark:border-surface-700 text-slate-500 dark:text-slate-400 hover:border-surface-200 dark:hover:border-surface-600"
                                                    )}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                            How can we improve?
                                        </label>
                                        <textarea
                                            required
                                            className="w-full h-32 p-3 text-sm bg-surface-50 dark:bg-surface-900 border-2 border-surface-100 dark:border-surface-700 rounded-xl focus:border-brand-500 outline-none transition-all dark:text-white"
                                            placeholder="Share your thoughts, report a bug, or suggest a feature..."
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                    {status === 'error' && (
                                        <div className="flex items-center gap-2 text-red-500 text-xs font-medium bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                                            <AlertCircle size={14} />
                                            Something went wrong. Please try again or email us.
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={status === 'loading' || !formData.description}
                                        className="w-full flex items-center justify-center gap-2 py-3 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-brand-500/20"
                                    >
                                        {status === 'loading' ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <Send size={18} />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
